import {
  type PayrollComputation,
  type PayrollLineResult,
  payrollDeductionItems,
  payrollExtraItems,
} from '@felix/domain';
import type { Content, CustomTableLayout, TDocumentDefinitions } from 'pdfmake/interfaces.js';
import { formatDay } from '../../format.js';
import { zonedStamp } from '../../inputs/lt-utc.js';
import { pdfBrandLogo } from '../../pdf-brand.js';
import { TOKENS } from '../../tokens.js';
import type { PayrollPayment, PayrollPeriod } from './types.js';
import {
  deductionTypeLabel,
  extraLabel,
  formatMoney,
  formatPercent,
  ITEM_COLUMN,
  itemAmount,
  itemTotals,
  monthLabel,
  visibleItems,
} from './view.js';

/** Çevirici: i18next `t`. */
export type PayrollT = (key: string, options?: Record<string, unknown>) => string;

export interface PayrollBrand {
  name: string | null;
  logo: string | null;
}

interface Common {
  vessel: { name: string };
  month: string;
  period: PayrollPeriod | null;
  /** Kapanış olayının hash'i (kapanmış dönemde). */
  closingHash: string | null;
  brand: PayrollBrand | null;
  /** Basım zamanı (UTC ISO). */
  printedAt: string;
  language: string;
}

export interface PayslipInput extends Common {
  lines: readonly PayrollLineResult[];
  payments: readonly PayrollPayment[];
}

export interface NetListInput extends Common {
  computation: PayrollComputation;
}

const FONT_SIZE = 9;
const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};
const FACTS: CustomTableLayout = { ...LINES, fillColor: () => null, hLineWidth: () => 0.4 };

/** Ortak başlık, durum ve alt bilgi (ADR 0057 §7 deseni). */
function documentOf(
  t: PayrollT,
  input: Common,
  title: string,
  body: Content[],
  orientation: 'portrait' | 'landscape',
): TDocumentDefinitions {
  const k = (key: string, options?: Record<string, unknown>) => t(`payroll.pdf.${key}`, options);
  const logo = pdfBrandLogo(input.brand, { fit: [48, 48], width: 56 });
  const period = monthLabel(input.month, input.language);
  const status =
    input.period?.status === 'closed'
      ? k('closed', {
          at: input.period.closedAt ? `${zonedStamp(input.period.closedAt)} UTC` : '',
          hash: (input.closingHash ?? '').slice(0, 12),
        })
      : k('open');
  const printed = k('printed', { at: `${zonedStamp(input.printedAt)} UTC` });
  return {
    pageSize: 'A4',
    pageOrientation: orientation,
    pageMargins: [36, 36, 36, 44],
    defaultStyle: { font: 'Roboto', fontSize: FONT_SIZE, color: TOKENS['navy-900'] },
    info: { title: `${title} ${period}`, subject: input.vessel.name, creator: 'Felixmate' },
    styles: {
      company: { fontSize: 10, bold: true, color: TOKENS['text-secondary'] },
      title: { fontSize: 15, bold: true, margin: [0, 2, 0, 2] },
      section: { fontSize: 10.5, bold: true, margin: [0, 10, 0, 4] },
      label: { color: TOKENS['text-secondary'] },
      muted: { color: TOKENS['text-secondary'], fontSize: 8 },
      mark: { bold: true, color: TOKENS['danger-fg'], fontSize: 9 },
      total: { bold: true },
    },
    content: [
      {
        columns: [
          ...(logo ? [logo] : []),
          {
            width: '*',
            stack: [
              { text: input.brand?.name ?? '', style: 'company' },
              { text: title, style: 'title' },
              { text: `${input.vessel.name} · ${period}`, style: 'muted' },
            ],
          },
          {
            width: 'auto',
            stack: [
              {
                text: status,
                style: input.period?.status === 'closed' ? 'muted' : 'mark',
                alignment: 'right' as const,
              },
            ],
          },
        ],
        columnGap: 10,
        margin: [0, 0, 0, 8],
      },
      ...body,
    ],
    footer: (page: number, pages: number) => ({
      columns: [
        { text: `${printed} · Felixmate`, fontSize: 7, color: TOKENS['text-secondary'] },
        {
          text: k('page', { page, pages }),
          alignment: 'right',
          fontSize: 7,
          color: TOKENS['text-secondary'],
        },
      ],
      margin: [36, 12, 36, 0],
    }),
  };
}

/**
 * Ücret pusulası (ADR 0061 §7) [MLC 2006 Std A2.2 ¶2]: kişi-ay; ortağın sırasıyla (M16b, ADR 0066)
 * kazançlar (Wages, varsa Extra), kesintiler (Home Allotment alıcısıyla, Cash Advance ve Slopchest
 * satırları, varsa diğer kesintiler kalem kalem; ADR 0080), Balance, ödemeler, dönem durumu ve hash, gemiadamı ve kaptan
 * imza kutuları. Birden çok kişi verilirse her biri yeni sayfada. Saf işlev: gemi ve
 * ofis aynı tanımı kullanır; pdfmake istemcide, internetsiz çalışır.
 */
export function payslipPdf(t: PayrollT, input: PayslipInput): TDocumentDefinitions {
  const k = (key: string, options?: Record<string, unknown>) => t(`payroll.pdf.${key}`, options);
  const money = (minor: number) => formatMoney(minor, input.language);
  const body: Content[] = [];
  input.lines.forEach((line, index) => {
    const own = input.payments.filter((p) => p.userId === line.userId);
    const amountRow = (label: string, minor: number, negative = false): Content[] => [
      { text: label },
      { text: `${negative && minor > 0 ? '−' : ''}${money(minor)}`, alignment: 'right' },
    ];
    // Ortağın sırası (M16b, ADR 0066): Wages, varsa Extra; Home Allotment, Cash Advance,
    // Slopchest, varsa diğer kesinti; Balance
    const earnings: Content[][] = [
      amountRow(k('wages', { days: line.daysOnBoard, wage: money(line.monthlyWage) }), line.basic),
    ];
    // Extra kalem kalem: tür, açıklama, miktar × birim ücret (ADR 0068 §5)
    for (const item of payrollExtraItems(line)) {
      earnings.push(
        amountRow(
          k('extraItem', { label: extraLabel(item, (key) => t(key), input.language) }),
          item.amount,
        ),
      );
    }
    earnings.push([
      { text: k('totalEarnings'), style: 'total' },
      { text: money(line.earnings), alignment: 'right', style: 'total' },
    ]);
    const deductions: Content[][] = [
      ...line.allotments.map((a) =>
        amountRow(
          `${k('homeAllotment', { name: a.beneficiaryName })}${
            a.basis === 'percent' ? ` (${formatPercent(a.percentBp, input.language)})` : ''
          }`,
          a.amount,
          true,
        ),
      ),
      ...line.advanceEntries.map((e) =>
        amountRow(
          `${k('cashAdvance')} ${formatDay(e.day)}${e.description ? ` · ${e.description}` : ''}`,
          e.amount,
          true,
        ),
      ),
      ...line.slopchestEntries.map((e) =>
        amountRow(
          `${k('slopchest')} ${formatDay(e.day)}${e.description ? ` · ${e.description}` : ''}`,
          e.amount,
          true,
        ),
      ),
    ];
    // Diğer kesintiler kalem kalem (ADR 0080 §5): satırın eski alanı, sonra internet, telefon,
    // şirket türü vb. gün sırasıyla [MLC 2006 Std A2.2 ¶2]
    for (const item of payrollDeductionItems(line)) {
      deductions.push(
        item.legacy
          ? amountRow(
              item.description
                ? `${k('otherDeductions')}: ${item.description}`
                : k('otherDeductions'),
              item.amount,
              true,
            )
          : amountRow(
              k('deductionItem', {
                label: deductionTypeLabel(item, (key) => t(key), input.language),
                day: item.day ? formatDay(item.day) : '',
              }).trim() + (item.description ? ` · ${item.description}` : ''),
              item.amount,
              true,
            ),
      );
    }
    if (deductions.length === 0) deductions.push([{ text: k('noDeductions') }, { text: '' }]);
    deductions.push([
      { text: k('totalDeductions'), style: 'total' },
      { text: `−${money(line.deductions)}`, alignment: 'right', style: 'total' },
    ]);

    body.push(
      {
        table: {
          widths: [90, '*', 90, '*'],
          body: [
            [
              { text: k('crew'), style: 'label' },
              { text: line.name, bold: true },
              { text: k('rank'), style: 'label' },
              { text: line.rank ?? '—' },
            ],
            [
              { text: k('vessel'), style: 'label' },
              { text: input.vessel.name },
              { text: k('period'), style: 'label' },
              { text: monthLabel(input.month, input.language) },
            ],
            [
              { text: k('currency'), style: 'label' },
              { text: line.currency },
              { text: k('days'), style: 'label' },
              { text: String(line.daysOnBoard) },
            ],
          ],
        },
        layout: FACTS,
        ...(index > 0 ? { pageBreak: 'before' as const } : {}),
      },
      {
        columns: [
          {
            width: '*',
            stack: [
              { text: k('earnings'), style: 'section' },
              { table: { widths: ['*', 80], body: earnings }, layout: FACTS },
            ],
          },
          {
            width: '*',
            stack: [
              { text: k('deductions'), style: 'section' },
              { table: { widths: ['*', 80], body: deductions }, layout: FACTS },
            ],
          },
        ],
        columnGap: 16,
      },
      {
        table: {
          widths: ['*', 120],
          body: [
            [
              { text: k('balance'), bold: true, fontSize: 11 },
              {
                text: `${line.currency} ${money(line.net)}`,
                bold: true,
                fontSize: 11,
                alignment: 'right',
              },
            ],
          ],
        },
        layout: FACTS,
        margin: [0, 10, 0, 0],
      },
      { text: k('payments'), style: 'section' },
      own.length === 0
        ? { text: k('noPayments'), style: 'muted' }
        : {
            table: {
              headerRows: 1,
              widths: [60, '*', '*', '*', 80],
              body: [
                [k('date'), k('type'), k('method'), k('reference'), k('amount')].map((h) => ({
                  text: h,
                  bold: true,
                })),
                ...own.map((p) => [
                  { text: formatDay(p.paymentDay) },
                  { text: t(`payroll.paymentTypes.${p.paymentType}`) },
                  { text: t(`payroll.methods.${p.method}`) },
                  { text: p.reference ?? '—' },
                  { text: `${p.currency} ${money(p.amount)}`, alignment: 'right' as const },
                ]),
              ],
            },
            layout: LINES,
          },
      { text: k('statementNote'), style: 'muted', margin: [0, 10, 0, 0] },
      {
        columns: [
          { width: '*', stack: [{ text: k('seafarerSign'), style: 'label' }, signatureBox()] },
          { width: '*', stack: [{ text: k('masterSign'), style: 'label' }, signatureBox()] },
        ],
        columnGap: 24,
        margin: [0, 24, 0, 0],
      },
    );
  });
  return documentOf(t, input, k('payslip'), body, 'portrait');
}

function signatureBox(): Content {
  return {
    canvas: [
      { type: 'line', x1: 0, y1: 36, x2: 220, y2: 36, lineWidth: 0.6, lineColor: TOKENS.border },
    ],
  };
}

/**
 * Dönemin net ödeme listesi (ADR 0061 §7 "Payroll Reports"): kişi, rütbe, para birimi, ortağın
 * kalemleri sırasıyla (M16b, ADR 0066; Extra ve diğer kesinti yalnız varsa) ve toplam ödenecek;
 * para birimine göre toplam. A4 yatay.
 */
export function netPaymentListPdf(t: PayrollT, input: NetListInput): TDocumentDefinitions {
  const k = (key: string, options?: Record<string, unknown>) => t(`payroll.pdf.${key}`, options);
  const c = (key: string) => t(`payroll.columns.${key}`);
  const money = (minor: number) => formatMoney(minor, input.language);
  const right = (text: string, bold = false) => ({ text, alignment: 'right' as const, bold });
  const items = visibleItems(input.computation.lines);
  const body: Content[] = [
    {
      table: {
        headerRows: 1,
        widths: [16, '*', 76, 30, ...items.map(() => 58), 64],
        body: [
          [
            '#',
            c('name'),
            c('rank'),
            c('currency'),
            ...items.map((item) => c(ITEM_COLUMN[item])),
            c('payable'),
          ].map((h) => ({ text: h, bold: true })),
          ...input.computation.lines.map((l, i) => [
            { text: String(i + 1) },
            { text: l.name },
            { text: l.rank ?? '—' },
            { text: l.currency },
            ...items.map((item) => right(money(itemAmount(l, item)), item === 'balance')),
            right(money(l.net + l.allotmentTotal)),
          ]),
          ...itemTotals(input.computation).map((tot) => [
            { text: '' },
            { text: k('total'), bold: true },
            { text: k('people', { count: tot.people }) },
            { text: tot.currency, bold: true },
            ...items.map((item) => right(money(tot[item]), true)),
            right(money(tot.payable), true),
          ]),
        ],
      },
      layout: LINES,
    },
    { text: k('netListNote'), style: 'muted', margin: [0, 8, 0, 0] },
  ];
  return documentOf(t, input, k('netList'), body, 'landscape');
}

/**
 * Dosya adı: yalnız ASCII harf, rakam, nokta, tire ve alt çizgi (gemi uygulamasının kaydetme
 * penceresi başka karakteri reddeder; `saved-file.ts`). Aksanlar düşer (ç → c, ş → s).
 */
export function payrollPdfFileName(...parts: string[]): string {
  const base = parts
    .join('-')
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'I')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
  return `${base || 'payroll'}.pdf`;
}
